import { diContainer } from '../DIContainer';
import { AppStorageKey, OrderTicketStatus } from '../types';
import Order from '../models/Order';
import OrderTicket from '../models/OrderTicket';
import CashRegister from '../models/CashRegister';
import Terminal from '../models/Terminal';
import { Shift } from '../models/Shift';
import { Store } from '../wrappers';
import moment from 'moment-timezone';
import { dateFormatter } from '../utilities/dateFormatter';
const { getTimezone } = dateFormatter();
const OrderService = {
    /**
     * Set Latest Order Number
     */
    async setLatestNumber(number) {
        const timezone = getTimezone();
        const year = moment().tz(timezone).format('YYYY');
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.lastOrderNumber, `${number}/${year}`);
    },
    /**
     * Get Next Order Number
     */
    async getNextNumber() {
        const appStorage = diContainer.resolve('appStorage');
        const timezone = getTimezone();
        const currentDate = moment().tz(timezone);
        const curNumber = await appStorage.local().getItem(AppStorageKey.lastOrderNumber);
        let nextNumber = curNumber ? parseInt(curNumber.split('/')[0]) + 1 : 1;
        // check latest order
        const numberedOrders = Order.all()
            .filter(order => {
            const orderYear = moment.utc(order.last_activity_at).tz(timezone).year();
            return !!order.number && orderYear == currentDate.year() && order.cash_register_id === CashRegister.current()?.id;
        })
            .sort((o1, o2) => (o2.number || 0) - (o1.number || 0));
        const lastOrder = numberedOrders.length > 0 ? numberedOrders[0] : null;
        if (lastOrder && (lastOrder.number || 0) >= parseInt('' + nextNumber)) {
            nextNumber = (lastOrder.number || 0) + 1;
        }
        // check if we entered the new year
        if (curNumber && currentDate.format('YYYY') > curNumber.split('/')[1]) {
            nextNumber = 1;
        }
        return nextNumber;
    },
    /**
     * Set Latest Internal Order Number
     */
    async setLatestInternalNumber(number) {
        const timezone = getTimezone();
        const year = moment().tz(timezone).format('YYYY');
        const internalNumber = ('' + number).split('/')[0];
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.lastOrderInternalNumber, `${internalNumber}/${year}`);
    },
    /**
     * Get Next Internal Order Number
     */
    async getNextInternalNumber() {
        const appStorage = diContainer.resolve('appStorage');
        const timezone = getTimezone();
        const currentDate = moment().tz(timezone);
        const curNumber = await appStorage.local().getItem(AppStorageKey.lastOrderInternalNumber);
        let nextNumber = curNumber ? parseInt(curNumber.split('/')[0]) + 1 : 1;
        // check latest order
        const numberedOrders = Order.all()
            .filter(order => {
            const orderYear = moment.utc(order.last_activity_at).tz(timezone).year();
            return !!order.internal_number && !order.isProforma() && orderYear == currentDate.year();
        })
            .sort((o1, o2) => parseInt(o2.internal_number || '0') - parseInt(o1.internal_number || '0'));
        const lastOrder = numberedOrders.length > 0 ? numberedOrders[0] : null;
        if (lastOrder && parseInt(lastOrder.internal_number || '0') >= parseInt('' + nextNumber)) {
            nextNumber = parseInt(lastOrder.internal_number || '0') + 1;
        }
        // check if we entered the new year
        if (curNumber && currentDate.format('YYYY') > curNumber.split('/')[1]) {
            nextNumber = 1;
        }
        return nextNumber;
    },
    /**
     * Get Latest Order Date
     */
    async getLatestOrderDate() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.lastOrderDate);
    },
    /**
     * Set Latest Order Date
     */
    setLatestOrderDate(date) {
        const appStorage = diContainer.resolve('appStorage');
        appStorage.local().setItem(AppStorageKey.lastOrderDate, date);
    },
    /**
     * Compute the callout code stored on the Order/OrderTicket at issue time.
     *
     * Format: `<letter><sequence>` when the venue has >1 allocating terminal
     * (cashRegister or orderingDevice — the types that issue orders/tickets),
     * else just `<sequence>`. Example: "5" (single-terminal venue), "A5"/"B5"
     * (multi-terminal venue).
     *
     * The letter is namespaced per TERMINAL, not per cash register: an
     * ordering device shares its register's shift (and therefore the shared
     * sequence count below), so two terminals on one register could compute
     * the same number concurrently — especially offline, where LAN sync
     * can't close the window. Disjoint letters make that harmless: orders
     * don't record their issuing terminal, and they don't need to —
     * uniqueness needs only (a) a terminal always sees its OWN previous
     * allocations in its local store (so its numbers strictly increase) and
     * (b) different terminals carry different letters. A racy shared count
     * can then only produce gaps (A5, B5, then A7), never collisions.
     *
     * Allocation is run ONCE at the moment the order/ticket is committed and
     * stored as the full string. Render sites read it verbatim — they never
     * recompute the letter.
     *
     * The sequence is a *reservation*, not a snapshot of issued orders: it
     * counts issued shift orders PLUS pending (not-yet-issued) OrderTickets
     * that already hold a callout code. A hub ticket reserves its number at
     * submit/fire time but does not become an `Order` until `completeAndIssue`
     * at courier pickup, so a plain `getOrders().length` would let the next
     * dine-in order — or the next hub ticket — reuse that number. Counting the
     * reserved tickets closes that gap.
     *
     * The "already holds a callout code" predicate self-excludes the current
     * allocation: at `fireToKitchen`/`submitAsTicket` the ticket being
     * allocated has no code yet (the very reason we're here), so it is never
     * double-counted. Once a ticket is issued it stops counting here and is
     * picked up by `getOrders()` instead — and it carries the same reserved
     * code, since `completeAndIssue` copies rather than recomputes. "Issued"
     * must be read off `order_hash` as well as `order_id`: an order issued
     * offline (or past the sync deadline) comes back with only its local hash,
     * and its Order already counts via the shift's locally-stamped shift_hash —
     * counting the ticket too would inflate every later sequence by one.
     * Tickets are venue-wide (no register binding), so every
     * terminal counts all reserved tickets; that only widens gaps within each
     * terminal's own letter namespace — it never collides, because the letter
     * prefix keeps allocators apart.
     *
     * Tickets that ENDED without ever becoming an Order keep their reservation
     * — cancelled ones, and equally the ones a platform completed or handed to
     * a courier before anyone issued a receipt. Such a ticket is counted by
     * neither `getOrders()` nor the active-ticket predicate, so dropping it
     * shifts the sequence down and the next allocation REUSES a code already
     * printed and shouted (e.g. tickets 1,3,4,5 pending + orders 2,6,7,8
     * issued, cancel ticket 3 → next = 4+3+1 = 8, colliding with live order 8;
     * live QA 2026-08-04 hit the same thing via a delivered-but-unfiscalized
     * ticket). How long the reservation lives follows how long the hub keeps
     * the ticket VISIBLE, because a collision is only possible against
     * something still on screen:
     *
     * - Cancelled tickets evict at the shift boundary, so they reserve only
     *   within the shift they ended in (`created_at` covers the common
     *   started-and-ended-this-shift case offline-safely; `updated_at` covers
     *   tickets created earlier and cancelled in this shift — a harmless gap).
     * - Delivered/picked-up tickets that were never fiscalized stay in the hub
     *   until someone issues them, however many shifts that takes — so they
     *   hold their reservation just as long. That inflates later sequences by
     *   one per outstanding ticket (gaps, never collisions) and the count
     *   drops back the moment the receipt is issued.
     */
    computeCalloutCode() {
        const curShift = Shift.current();
        const issuedCount = curShift ? curShift.getOrders().length : 0;
        const shiftOpenedMs = curShift?.opened_at ? moment.utc(curShift.opened_at).valueOf() : null;
        const reservedTickets = OrderTicket.all().filter(t => {
            if (t.order_id !== null || t.order_hash !== null || !t.callout_code)
                return false;
            if (t.isActive())
                return true;
            if (t.status !== OrderTicketStatus.cancelled)
                return true;
            if (shiftOpenedMs === null)
                return false;
            const createdMs = t.created_at ? moment.utc(t.created_at).valueOf() : 0;
            const updatedMs = t.updated_at ? moment.utc(t.updated_at).valueOf() : 0;
            return Math.max(createdMs, updatedMs) >= shiftOpenedMs;
        }).length;
        const sequence = issuedCount + reservedTickets + 1;
        // Letter = this terminal's index among the venue's allocating
        // terminals. The Terminal list is server-synced and id-sorted, so
        // every terminal computes the same letter map; new terminals get
        // higher ids and never shift existing letters.
        const allocators = Terminal.all()
            .filter(t => t.isCashRegister() || t.isOrderingDevice())
            .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        if (allocators.length <= 1)
            return String(sequence);
        const curTerminalId = Store.get('terminal')?.id ?? null;
        if (curTerminalId === null)
            return String(sequence);
        const idx = allocators.findIndex(t => t.id === curTerminalId);
        if (idx < 0)
            return String(sequence); // terminal since removed
        if (idx > 25)
            return String(sequence); // YAGNI: degrade gracefully past 26 terminals
        const letter = String.fromCharCode(65 + idx);
        return `${letter}${sequence}`;
    },
};
export default OrderService;
export { OrderService };
//# sourceMappingURL=OrderService.js.map